/**
 * A piece of parsed markdown: plain text, bold text, italic text, or a link.
 * Consumed by {@link MarkdownText} to render real DOM nodes instead of injecting
 * raw HTML with `v-html`, so the supported subset stays XSS-safe.
 */
export type MarkdownSegment =
  | { type: "text"; text: string }
  | { type: "strong"; text: string }
  | { type: "em"; text: string }
  | { type: "link"; text: string; href: string };

// Each pattern matches ONE kind of token so it is easy to read in isolation.
const BOLD_PATTERN = /\*\*([^*]+?)\*\*/g; // CommonMark `**bold**`, group 1 = text
const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g; // group 1: label, group 2: url
const EM_PATTERN = /\*([^*]+?)\*/g; // CommonMark `*italic*` (bold `**` is consumed first)

// Schemes we allow inside an href. Anything else (e.g. `javascript:`) is treated
// as unsafe and rendered as plain text instead of a link.
const SAFE_HREF_PATTERN = /^(https?:|mailto:)/i;

// Any one of the tokens `parseMarkdown` understands. Kept non-global so repeated
// `.test()` calls don't carry `lastIndex` state between checks.
const MARKDOWN_TOKEN_PATTERN = /\*\*[^*]+?\*\*|\*[^*]+?\*|\[[^\]]+\]\([^)]+\)/;

/**
 * Returns true when `input` contains at least one supported markdown token
 * (`**bold**`, `*italic*`, or `[label](url)`). Lets callers decide whether a
 * value needs parsing without passing an explicit flag, so a string is only
 * treated as markdown when it actually looks like markdown.
 */
export function hasMarkdown(input: string): boolean {
  return Boolean(input) && MARKDOWN_TOKEN_PATTERN.test(input);
}

/**
 * Returns true when an href is safe to render as a link: site-relative paths and
 * http(s)/mailto URLs are allowed, every other scheme is rejected.
 */
export function isSafeHref(href: string): boolean {
  const value = href.trim();
  return value.startsWith("/") || SAFE_HREF_PATTERN.test(value);
}

interface Chunk {
  /** The plain-text run between matches (empty string for a matched chunk). */
  text: string;
  /** The regex match for this chunk, or null when it is a plain-text run. */
  match: RegExpMatchArray | null;
}

/**
 * Walks `pattern` across `text` and returns its pieces in order: each match and
 * the plain text between the matches. `pattern` must be a global (`/g`) regex.
 */
function splitAround(text: string, pattern: RegExp): Chunk[] {
  const chunks: Chunk[] = [];
  let cursor = 0;

  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      chunks.push({ text: text.slice(cursor, start), match: null });
    }
    chunks.push({ text: match[0], match });
    cursor = start + match[0].length;
  }

  if (cursor < text.length) {
    chunks.push({ text: text.slice(cursor), match: null });
  }

  return chunks;
}

/**
 * Splits a plain-text run around `*italic*` spans, pushing `em`/`text` segments.
 * Runs last so bold (`**`) and links are already consumed.
 */
function pushItalicsAndText(segments: MarkdownSegment[], run: string): void {
  for (const emChunk of splitAround(run, EM_PATTERN)) {
    if (emChunk.match) {
      segments.push({ type: "em", text: emChunk.match[1] });
    } else if (emChunk.text) {
      segments.push({ type: "text", text: emChunk.text });
    }
  }
}

/**
 * Parses a raw string into safe, typed segments for the small markdown subset we
 * support: bold (`**…**`), italics (`*…*`), and links (`[label](url)`).
 *
 * It works in ordered passes: first split around bold blocks, then split each
 * remaining run around links, then split the leftover plain text around italics.
 * Plain strings with no markup return a single text segment, so callers can
 * render every value the same way.
 */
export function parseMarkdown(input: string): MarkdownSegment[] {
  if (!input) {
    return [];
  }

  const segments: MarkdownSegment[] = [];

  for (const boldChunk of splitAround(input, BOLD_PATTERN)) {
    if (boldChunk.match) {
      segments.push({ type: "strong", text: boldChunk.match[1] });
      continue;
    }

    // Not bold: look for markdown links inside this plain-text run.
    for (const linkChunk of splitAround(boldChunk.text, LINK_PATTERN)) {
      if (!linkChunk.match) {
        pushItalicsAndText(segments, linkChunk.text);
        continue;
      }

      const [, label, url] = linkChunk.match;
      if (isSafeHref(url)) {
        segments.push({ type: "link", text: label, href: url.trim() });
      } else {
        // Unsafe link: keep the label as text so no wording is lost.
        segments.push({ type: "text", text: label });
      }
    }
  }

  return segments;
}
